import type { ReactNode } from 'react';
import type { AlertDetailResponse } from '@/types/alerts';

/**
 * Default evidence renderer.
 *
 * Renders any unknown rule_id's `evidence` JSONB as a key/value table.
 * - Scalars (string/number/boolean) print verbatim.
 * - null prints as "—" (em dash) with muted text.
 * - Arrays and objects pretty-print as compact JSON in <code>.
 *
 * Per-rule custom renderers (e.g. for OEM-overshoot, midnight-runs)
 * land in PR 3 under `./custom/<rule_id>.tsx`. PR 2 ships only this
 * default so every section renders something useful today.
 */

function renderValue(v: unknown): ReactNode {
  if (v === null || v === undefined) {
    return <span className="text-muted-foreground">—</span>;
  }
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
    return <span className="font-mono text-xs">{String(v)}</span>;
  }
  // arrays + objects → compact JSON
  return (
    <code className="block max-w-full overflow-x-auto rounded bg-muted px-2 py-1 font-mono text-[11px]">
      {JSON.stringify(v)}
    </code>
  );
}

export function defaultEvidenceRenderer(
  evidence: Record<string, unknown> | null | undefined,
  _alert: AlertDetailResponse,
): ReactNode {
  const entries = Object.entries(evidence ?? {});
  if (entries.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No structured evidence payload was captured for this alert.
      </p>
    );
  }
  return (
    <div className="overflow-hidden rounded-md border">
      <table className="w-full text-sm">
        <tbody>
          {entries.map(([key, value]) => (
            <tr
              key={key}
              className="border-b last:border-0 odd:bg-muted/30"
            >
              <td className="w-1/3 max-w-[14rem] truncate px-3 py-2 align-top font-mono text-xs text-muted-foreground">
                {key}
              </td>
              <td className="px-3 py-2 align-top">{renderValue(value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
